import { Injectable } from '@angular/core';
import {
  CollectionIdempotentOperation,
  getCollectionIdempotentOperationIdentity,
} from './collection-request-idempotency';

interface ActiveCollectionIdempotencyIntent {
  operation: CollectionIdempotentOperation;
  key: string;
}

@Injectable({
  providedIn: 'root',
})
export class CollectionRequestIdempotencyService {
  private readonly activeIntents = new Map<
    string,
    ActiveCollectionIdempotencyIntent
  >();

  getOrCreateKey(operation: CollectionIdempotentOperation): string {
    const identity = getCollectionIdempotentOperationIdentity(operation);
    const activeIntent = this.activeIntents.get(identity);

    if (activeIntent) {
      return activeIntent.key;
    }

    const key = this.createIdempotencyKey();
    this.activeIntents.set(identity, { operation, key });
    return key;
  }

  peekKey(operation: CollectionIdempotentOperation): string | null {
    const identity = getCollectionIdempotentOperationIdentity(operation);
    return this.activeIntents.get(identity)?.key ?? null;
  }

  clear(operation: CollectionIdempotentOperation): void {
    const identity = getCollectionIdempotentOperationIdentity(operation);
    this.activeIntents.delete(identity);
  }

  clearCollectionIntents(collectionId: string): void {
    this.clearWhere((operation) => operation.collectionId === collectionId);
  }

  clearSubmitAuthInteractionIntents(
    collectionId: string,
    connectionId?: string,
    authInteractionKind?: string,
  ): void {
    this.clearWhere(
      (operation) =>
        operation.kind === 'submit-auth-interaction' &&
        operation.collectionId === collectionId &&
        (!connectionId || operation.connectionId === connectionId) &&
        (authInteractionKind === undefined ||
          operation.authInteractionKind === authInteractionKind),
    );
  }

  private clearWhere(
    predicate: (operation: CollectionIdempotentOperation) => boolean,
  ): void {
    for (const [identity, intent] of this.activeIntents.entries()) {
      if (predicate(intent.operation)) {
        this.activeIntents.delete(identity);
      }
    }
  }

  private createIdempotencyKey(): string {
    const randomUUID = globalThis.crypto?.randomUUID;

    if (!randomUUID) {
      throw new Error(
        'Collection idempotency requires crypto.randomUUID support.',
      );
    }

    return randomUUID.call(globalThis.crypto);
  }
}
